import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { InfoTip } from "@/components/InfoTip";
import { formatDueDisplay } from "@/lib/format";
import type { ActionItem, ActionItemsOut } from "@/types/api";

type KindFilter = "all" | "overdue" | "due_soon";

function duePhrase(item: ActionItem): string {
  if (item.kind === "overdue") {
    if (item.days_until_due !== null) {
      return `Overdue by ${-item.days_until_due} day${-item.days_until_due === 1 ? "" : "s"}`;
    }
    return "Overdue";
  }
  if (item.days_until_due === null) return "Due soon";
  if (item.days_until_due === 0) return "Due today";
  return `Due in ${item.days_until_due} day${item.days_until_due === 1 ? "" : "s"}`;
}

function StatCard({
  label,
  value,
  tone,
  help,
}: {
  label: string;
  value: number;
  tone: "high" | "medium" | "ok";
  help: string;
}): JSX.Element {
  const toneClass =
    value === 0
      ? "text-severity-ok"
      : tone === "high"
        ? "text-severity-high"
        : tone === "medium"
          ? "text-severity-medium"
          : "text-severity-ok";
  return (
    <div className="card">
      <p className="card-title">
        {label}
        <InfoTip label={label} body={help} />
      </p>
      <p className={`text-3xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

/** Stat tiles + filterable action list. Shared by the tenant operator home
 * (live, single tenant) and the platform dashboard (nightly snapshot,
 * cross-tenant). `asOf` is shown only when provided (i.e. for the snapshot). */
export function ActionQueue({
  data,
  asOf,
}: {
  data: ActionItemsOut;
  asOf?: string;
}): JSX.Element {
  const [kind, setKind] = useState<KindFilter>("all");
  const [system, setSystem] = useState<string>("all");

  const items = useMemo(() => {
    const all = data.items ?? [];
    return all.filter(
      (i) => (kind === "all" || i.kind === kind) && (system === "all" || i.pws_id === system),
    );
  }, [data, kind, system]);

  const totals = useMemo(() => {
    const systems = data.systems ?? [];
    return {
      overdue: systems.reduce((n, s) => n + s.overdue, 0),
      dueSoon: systems.reduce((n, s) => n + s.due_soon, 0),
      needsInfo: systems.reduce((n, s) => n + s.needs_info, 0),
    };
  }, [data]);

  const multiSystem = (data.systems?.length ?? 0) > 1;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label="Overdue"
          value={totals.overdue}
          tone="high"
          help="Monitoring items whose due date has passed with no filing on record. These need action now — regulators treat missed samples as violations."
        />
        <StatCard
          label="Due in 30 days"
          value={totals.dueSoon}
          tone="medium"
          help="Tests coming due within the next 30 days. Schedule sample collection with your lab now to stay ahead."
        />
        <StatCard
          label="Needs info"
          value={totals.needsInfo}
          tone="ok"
          help="Schedule rows that can't be computed yet because a system attribute (e.g. treatment type) hasn't been filled in, or a filing couldn't be matched. Open the system page to resolve them."
        />
      </div>

      <section className="card">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <h2 className="card-title mb-0">Action queue</h2>
          {asOf && (
            <span className="text-xs text-ink-400">as of {formatDueDisplay(asOf)}</span>
          )}
          <div className="flex gap-1 ml-auto" role="group" aria-label="Filter by status">
            {(
              [
                ["all", "All"],
                ["overdue", "Overdue"],
                ["due_soon", "Due soon"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setKind(value)}
                className={`px-2.5 py-1 rounded-md text-xs ring-1 transition-colors ${
                  kind === value
                    ? "bg-ink-700 ring-ink-500 text-ink-100"
                    : "ring-ink-600 text-ink-300 hover:text-ink-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {multiSystem && (
            <select
              className="input w-auto py-1 text-sm"
              value={system}
              onChange={(e) => setSystem(e.target.value)}
              aria-label="Filter by system"
            >
              <option value="all">All systems</option>
              {data.systems.map((s) => (
                <option key={s.pws_id} value={s.pws_id}>
                  {s.pws_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-ink-300">
            {kind === "all" && system === "all"
              ? "Nothing needs attention right now — all monitoring is on schedule."
              : "Nothing matches this filter."}
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {items.map((item) => (
              <li key={`${item.pws_id}-${item.rule_id}`}>
                <Link
                  to={`/systems/${item.pws_id}`}
                  className="flex items-center gap-3 py-2.5 group"
                >
                  <span className={item.kind === "overdue" ? "pill-high" : "pill-medium"}>
                    {duePhrase(item)}
                  </span>
                  <span className="text-sm text-ink-100">{item.contaminant_name}</span>
                  <span className="text-xs text-ink-400">
                    {item.frequency}
                    {item.next_due ? ` · due ${formatDueDisplay(item.next_due)}` : ""}
                  </span>
                  {multiSystem && (
                    <span className="text-xs text-ink-300 ml-auto">{item.pws_name}</span>
                  )}
                  <span
                    className={`text-ink-400 group-hover:text-ink-100 ${multiSystem ? "" : "ml-auto"}`}
                    aria-hidden="true"
                  >
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
